import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Calendar, Clock, Mail, MessageCircle, ArrowLeft, Sparkles } from "lucide-react";
import { siteData } from "../data/mock";
import Footer from "../components/Footer";

const BrandLogo = () => (
  <Link to="/" className="flex items-center gap-2">
    <img
      src="https://customer-assets.emergentagent.com/job_career-ai-summit-1/artifacts/xp67b1sj_image.png"
      alt="Epsilon Executive Education"
      className="h-9 md:h-10 w-auto"
    />
  </Link>
);

const pad = (n) => String(n).padStart(2, "0");
const calcRemaining = (targetIso) => {
  const diff = Math.max(0, new Date(targetIso).getTime() - Date.now());
  return {
    days: Math.floor(diff / 86400000),
    hrs: Math.floor((diff / 3600000) % 24),
    min: Math.floor((diff / 60000) % 60),
    sec: Math.floor((diff / 1000) % 60),
  };
};

const ThankYou = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { webinar, footer } = siteData;
  const name = location.state?.name;
  const email = location.state?.email;
  const [t, setT] = useState(calcRemaining(webinar.target));

  useEffect(() => {
    document.title = "Thank You — Your Seat Is Reserved | Epsilon Executive Education";
    window.scrollTo(0, 0);
    const id = setInterval(() => setT(calcRemaining(webinar.target)), 1000);
    return () => clearInterval(id);
  }, [webinar.target]);

  return (
    <div className="min-h-screen bg-[#060b17] text-slate-200 overflow-x-hidden flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b hairline bg-[#060b17]/85 backdrop-blur-md">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 h-[70px] sm:h-[78px] flex items-center justify-between">
          <BrandLogo />
          <button
            onClick={() => navigate("/")}
            data-testid="back-home-btn"
            className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-[11px] sm:text-[12px] tracking-[0.2em] uppercase text-slate-300 hover:text-white border hairline hover:border-[#5EC7F5]/40 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Back to Home</span>
            <span className="sm:hidden">Home</span>
          </button>
        </div>
      </header>

      <main className="hero-radial flex-1 pt-[90px] sm:pt-[110px] pb-16 sm:pb-24 relative">
        {/* Grid overlay */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(120,200,220,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(120,200,220,0.4) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            maskImage: "radial-gradient(ellipse at 50% 30%, black 30%, transparent 75%)",
          }}
        />

        <div className="max-w-[900px] mx-auto px-5 sm:px-6 lg:px-10 relative">
          {/* Success chip */}
          <div className="flex justify-center">
            <div
              data-testid="thank-you-badge"
              className="inline-flex items-center gap-2 chip-blue px-4 py-2 rounded-full"
            >
              <CheckCircle2 className="w-4 h-4 text-[#5EC7F5]" />
              <span className="text-[10px] sm:text-[11px] tracking-[0.28em] sm:tracking-[0.32em] uppercase">
                Seat Reserved
              </span>
            </div>
          </div>

          {/* Heading */}
          <h1 className="font-display text-center text-[40px] sm:text-[56px] md:text-[72px] lg:text-[86px] leading-[0.98] mt-6 sm:mt-8 tracking-tight">
            <span className="text-gradient-title">Thank you</span>
            {name ? <span className="text-gradient-blue">, {name.split(" ")[0]}</span> : null}
            <span className="text-gradient-title">.</span>
          </h1>

          <p className="text-center text-slate-300 text-[16px] sm:text-[20px] md:text-[24px] mt-4 sm:mt-5 font-light px-2">
            You're in. Your seat for{" "}
            <span className="text-white">The AI Career Accelerator</span> is confirmed.
          </p>

          {email ? (
            <p
              data-testid="confirmation-email-text"
              className="text-center text-slate-400 text-[14px] sm:text-[15px] mt-4 max-w-[560px] mx-auto px-2"
            >
              We've sent a confirmation to{" "}
              <span className="text-[#5EC7F5]">{email}</span> with the joining link. Please
              check your inbox (and spam folder just in case).
            </p>
          ) : (
            <p className="text-center text-slate-400 text-[14px] sm:text-[15px] mt-4 max-w-[560px] mx-auto px-2">
              We've sent a confirmation email with the joining link. Please check your inbox
              (and spam folder just in case).
            </p>
          )}

          {/* Details card */}
          <div className="card-glow rounded-2xl p-5 sm:p-7 lg:p-8 mt-10 sm:mt-12 relative overflow-hidden">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10px] sm:text-[11px] tracking-[0.28em] sm:tracking-[0.32em] uppercase text-[#5EC7F5]">
                <Sparkles className="w-3.5 h-3.5" />
                Webinar Details
              </div>
              <span className="chip-blue text-[10px] tracking-[0.24em] uppercase px-3 py-1 rounded-full">
                Free
              </span>
            </div>

            <h2 className="font-display text-[24px] sm:text-[28px] md:text-[32px] leading-[1.05] mt-5 text-white">
              {webinar.title}
            </h2>
            <p className="text-slate-400 text-[13px] sm:text-sm mt-2">{webinar.subtitle}</p>

            <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-6">
              <div className="rounded-xl border hairline p-3 sm:p-4 bg-[#0a1626]/60">
                <div className="flex items-center gap-2 text-[9px] sm:text-[10px] tracking-[0.24em] sm:tracking-[0.28em] uppercase text-slate-400">
                  <Calendar className="w-3.5 h-3.5 text-[#5EC7F5]" /> Date
                </div>
                <div className="text-white font-display text-[18px] sm:text-[22px] md:text-[24px] leading-none mt-3">
                  {webinar.dateLabel}
                </div>
                <div className="text-slate-400 text-[10px] sm:text-[11px] tracking-[0.24em] sm:tracking-[0.28em] uppercase mt-2">
                  {webinar.dateSub}
                </div>
              </div>
              <div className="rounded-xl border hairline p-3 sm:p-4 bg-[#0a1626]/60">
                <div className="flex items-center gap-2 text-[9px] sm:text-[10px] tracking-[0.24em] sm:tracking-[0.28em] uppercase text-slate-400">
                  <Clock className="w-3.5 h-3.5 text-[#5EC7F5]" /> Time
                </div>
                <div className="text-white font-display text-[18px] sm:text-[22px] md:text-[24px] leading-none mt-3">
                  {webinar.timeLabel}
                </div>
                <div className="text-slate-400 text-[10px] sm:text-[11px] tracking-[0.24em] sm:tracking-[0.28em] uppercase mt-2">
                  {webinar.timeSub}
                </div>
              </div>
            </div>

            {/* Countdown */}
            <div className="mt-5 sm:mt-6 rounded-xl border hairline p-4 sm:p-5">
              <div className="text-[10px] tracking-[0.32em] uppercase text-slate-400 text-center">
                Starts In
              </div>
              <div className="flex items-center justify-between mt-4">
                {[
                  { v: t.days, l: "Days" },
                  { v: t.hrs, l: "Hrs" },
                  { v: t.min, l: "Min" },
                  { v: t.sec, l: "Sec" },
                ].map((item, i, arr) => (
                  <React.Fragment key={item.l}>
                    <div className="flex flex-col items-center min-w-[44px] sm:min-w-[60px]">
                      <span className="number-tick text-[32px] sm:text-[44px] md:text-[54px] leading-none font-medium">
                        {pad(item.v)}
                      </span>
                      <span className="mt-2 text-[9px] sm:text-[10px] tracking-[0.22em] sm:tracking-[0.28em] uppercase text-slate-400">
                        {item.l}
                      </span>
                    </div>
                    {i < arr.length - 1 && (
                      <span className="text-slate-600 text-lg sm:text-2xl -mt-4">·</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* Next steps */}
          <div className="mt-10 sm:mt-14">
            <div className="flex items-center justify-center gap-3">
              <span className="eyebrow-line" />
              <span className="text-[11px] tracking-[0.32em] uppercase text-[#5EC7F5]">
                What's Next
              </span>
              <span className="eyebrow-line" />
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              {[
                {
                  n: "01",
                  t: "Check your inbox",
                  d: "Your joining link is on its way. Whitelist admissions@epsilonexec.com so it doesn't land in spam.",
                },
                {
                  n: "02",
                  t: "Save the date",
                  d: `Block ${webinar.dateLabel}, ${webinar.timeLabel} ${webinar.timeSub} on your calendar. We'll send a reminder before we go live.`,
                },
                {
                  n: "03",
                  t: "Come with questions",
                  d: "The session includes live Q&A. Bring one problem you'd love to solve with AI.",
                },
              ].map((s) => (
                <div
                  key={s.n}
                  className="rounded-xl border hairline p-5 bg-[#0a1626]/50"
                >
                  <div className="big-number-outline text-[42px] sm:text-[48px] leading-none">
                    {s.n}
                  </div>
                  <h3 className="font-display text-[18px] sm:text-[20px] text-white mt-3">
                    {s.t}
                  </h3>
                  <p className="text-slate-400 text-[13px] leading-relaxed mt-2">{s.d}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
            <a
              href={`mailto:${footer.email}`}
              data-testid="thank-you-email-link"
              className="inline-flex items-center gap-2 text-[13px] sm:text-[14px] text-slate-300 hover:text-white transition-colors"
            >
              <Mail className="w-4 h-4 text-[#5EC7F5]" />
              {footer.email}
            </a>
            <span className="hidden sm:inline text-slate-600">·</span>
            <a
              href={`https://wa.me/${(footer.phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(
                "Hi Epsilon, I just registered for The AI Career Accelerator webinar."
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="thank-you-whatsapp-link"
              className="inline-flex items-center gap-2 text-[13px] sm:text-[14px] text-slate-300 hover:text-white transition-colors"
            >
              <MessageCircle className="w-4 h-4 text-[#5EC7F5]" />
              {footer.whatsapp}
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ThankYou;
